import { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import {
  api, formatApiError, CLASSIFICATION_LABELS, PRIORITY_LABELS, VISIBILITY_LABELS,
  REC_FREQ_LABELS, REC_OVERLAP_LABELS, WEEKDAYS_PT,
} from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  title: "", description: "", classification: "O", priority: "medium", visibility: "project",
  assignee_id: "", participants: [], story_points: "", estimated_hours: "",
  checklist: [], checklist_required: false,
  start_date: today(), freq_type: "weekly", interval: 1, weekdays: [new Date().getDay() === 0 ? 6 : new Date().getDay() - 1],
  day_of_month: 1, last_day: false, month: 1,
  end_type: "never", end_date: "", end_count: 1,
  due_offset_days: 0, overlap_policy: "allow", next_date: "",
};

// Create/edit a recurrence template. Reuses task field semantics.
export default function RecurrenceModal({ projectId, project, recurrenceId, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [newCheck, setNewCheck] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = !!recurrenceId;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!recurrenceId) return;
    (async () => {
      try {
        const { data } = await api.get(`/recurrences/${recurrenceId}`);
        const f = data.frequency || {};
        setForm({
          title: data.title || "", description: data.description || "",
          classification: data.classification || "O", priority: data.priority || "medium",
          visibility: data.visibility || "project", assignee_id: data.assignee_id || "",
          participants: (data.participants || []).map((p) => p.user_id),
          story_points: data.story_points ?? "", estimated_hours: data.estimated_hours ?? "",
          checklist: (data.checklist || []).map((c) => ({ text: c.text })),
          checklist_required: !!data.checklist_required,
          start_date: data.start_date || today(),
          freq_type: f.type || "weekly", interval: f.interval || 1,
          weekdays: f.weekdays || [0], day_of_month: f.day_of_month || 1,
          last_day: !!f.last_day, month: f.month || 1,
          end_type: (data.end || {}).type || "never", end_date: (data.end || {}).date || "",
          end_count: (data.end || {}).count || 1,
          due_offset_days: data.due_offset_days || 0, overlap_policy: data.overlap_policy || "allow",
          next_date: data.next_run_date || "",
        });
      } catch (e) { toast.error(formatApiError(e)); onClose?.(); }
    })();
  }, [recurrenceId]); // eslint-disable-line

  const toggleWeekday = (v) => {
    setForm((f) => {
      const has = f.weekdays.includes(v);
      return { ...f, weekdays: has ? f.weekdays.filter((x) => x !== v) : [...f.weekdays, v].sort() };
    });
  };
  const toggleParticipant = (uid) => {
    setForm((f) => {
      const has = f.participants.includes(uid);
      return { ...f, participants: has ? f.participants.filter((x) => x !== uid) : [...f.participants, uid] };
    });
  };
  const addCheck = () => {
    if (!newCheck.trim()) return;
    set("checklist", [...form.checklist, { text: newCheck.trim() }]);
    setNewCheck("");
  };
  const removeCheck = (i) => set("checklist", form.checklist.filter((_, idx) => idx !== i));

  const buildPayload = () => {
    const freq = { type: form.freq_type, interval: Number(form.interval) || 1 };
    if (form.freq_type === "weekly") freq.weekdays = form.weekdays;
    if (form.freq_type === "monthly") {
      freq.last_day = form.last_day;
      if (!form.last_day) freq.day_of_month = Number(form.day_of_month);
    }
    if (form.freq_type === "yearly") { freq.month = Number(form.month); freq.day_of_month = Number(form.day_of_month); }
    const end = { type: form.end_type };
    if (form.end_type === "date") end.date = form.end_date;
    if (form.end_type === "count") end.count = Number(form.end_count);
    const payload = {
      title: form.title, description: form.description, classification: form.classification,
      priority: form.priority, visibility: form.visibility,
      participants: form.participants, checklist: form.checklist,
      checklist_required: form.checklist_required, start_date: form.start_date,
      frequency: freq, end, due_offset_days: Number(form.due_offset_days) || 0,
      overlap_policy: form.overlap_policy,
    };
    if (form.assignee_id) payload.assignee_id = form.assignee_id; else if (isEdit) payload.clear_assignee = true;
    payload.story_points = form.story_points === "" ? null : Number(form.story_points);
    payload.estimated_hours = form.estimated_hours === "" ? null : Number(form.estimated_hours);
    if (isEdit && form.next_date) payload.next_date = form.next_date;
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/recurrences/${recurrenceId}`, buildPayload());
        toast.success("Recorrência atualizada. Apenas ocorrências futuras são afetadas.");
      } else {
        await api.post(`/projects/${projectId}/recurrences`, buildPayload());
        toast.success("Recorrência criada.");
      }
      onSaved?.();
      onClose?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const participants = project?.participants || [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="recurrence-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-3xl my-8 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-xl font-semibold tracking-tight">{isEdit ? "Editar recorrência" : "Nova recorrência"}</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="recurrence-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="overflow-y-auto p-6 space-y-6">
          {/* Conteúdo da tarefa */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Conteúdo da tarefa gerada</h3>
            <input required placeholder="Título" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="rec-title"
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <textarea rows={3} placeholder="Descrição" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="rec-description"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Classificação (E/D/O/Q)">
                <select value={form.classification} onChange={(e) => set("classification", e.target.value)} data-testid="rec-classification" className="prop-select">
                  {Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                </select>
              </Field>
              <Field label="Prioridade">
                <select value={form.priority} onChange={(e) => set("priority", e.target.value)} data-testid="rec-priority" className="prop-select">
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Visibilidade">
                <select value={form.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="rec-visibility" className="prop-select">
                  {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Responsável previsto">
                <select value={form.assignee_id} onChange={(e) => set("assignee_id", e.target.value)} data-testid="rec-assignee" className="prop-select">
                  <option value="">Sem responsável</option>
                  {participants.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Story points"><input type="number" min="0" value={form.story_points} onChange={(e) => set("story_points", e.target.value)} data-testid="rec-points" className="prop-select" /></Field>
              <Field label="Horas estimadas"><input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} data-testid="rec-hours" className="prop-select" /></Field>
            </div>
            {/* Participantes explícitos */}
            <Field label="Participantes explícitos">
              <div className="flex flex-wrap gap-2" data-testid="rec-participants">
                {participants.length === 0 && <span className="text-xs text-slate-400">Nenhum participante no projeto.</span>}
                {participants.map((p) => (
                  <label key={p.user_id} className={`inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded-md border cursor-pointer ${form.participants.includes(p.user_id) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"}`}>
                    <input type="checkbox" className="hidden" checked={form.participants.includes(p.user_id)} onChange={() => toggleParticipant(p.user_id)} data-testid={`rec-participant-${p.user_id}`} />
                    {p.name}
                  </label>
                ))}
              </div>
            </Field>
            {/* Checklist */}
            <Field label="Checklist (itens nascem desmarcados)">
              <ul className="space-y-1 mb-2" data-testid="rec-checklist">
                {form.checklist.map((c, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded-md px-2 py-1">
                    <span className="flex-1">{c.text}</span>
                    <button type="button" onClick={() => removeCheck(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <input value={newCheck} onChange={(e) => setNewCheck(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCheck(); } }} placeholder="Novo item" data-testid="rec-check-input" className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-sm" />
                <button type="button" onClick={addCheck} data-testid="rec-check-add" className="h-8 px-2 rounded-md border border-slate-200 text-sm hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-2">
                <input type="checkbox" checked={form.checklist_required} onChange={(e) => set("checklist_required", e.target.checked)} data-testid="rec-checklist-required" />
                Obrigatório para conclusão da tarefa
              </label>
            </Field>
          </section>

          {/* Programação */}
          <section className="space-y-3 border-t border-slate-100 pt-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Programação</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Data inicial"><input type="date" required value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="rec-start-date" className="prop-select" /></Field>
              <Field label="Frequência">
                <select value={form.freq_type} onChange={(e) => set("freq_type", e.target.value)} data-testid="rec-freq-type" className="prop-select">
                  {Object.entries(REC_FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label={form.freq_type === "weekly" ? "A cada N semanas" : form.freq_type === "monthly" ? "A cada N meses" : form.freq_type === "yearly" ? "A cada N anos" : "A cada N dias"}>
                <input type="number" min="1" value={form.interval} onChange={(e) => set("interval", e.target.value)} data-testid="rec-interval" className="prop-select" />
              </Field>
            </div>

            {form.freq_type === "weekly" && (
              <Field label="Dias da semana">
                <div className="flex flex-wrap gap-1.5" data-testid="rec-weekdays">
                  {WEEKDAYS_PT.map((d) => (
                    <button type="button" key={d.v} onClick={() => toggleWeekday(d.v)} data-testid={`rec-weekday-${d.v}`}
                      className={`h-8 w-12 rounded-md text-xs font-medium border ${form.weekdays.includes(d.v) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{d.label}</button>
                  ))}
                </div>
              </Field>
            )}
            {form.freq_type === "monthly" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Dia do mês">
                  <input type="number" min="1" max="31" disabled={form.last_day} value={form.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} data-testid="rec-day-of-month" className="prop-select disabled:bg-slate-100" />
                </Field>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 self-end pb-2">
                  <input type="checkbox" checked={form.last_day} onChange={(e) => set("last_day", e.target.checked)} data-testid="rec-last-day" />
                  Último dia do mês
                </label>
              </div>
            )}
            {form.freq_type === "yearly" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Mês">
                  <select value={form.month} onChange={(e) => set("month", e.target.value)} data-testid="rec-month" className="prop-select">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Dia"><input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} data-testid="rec-year-day" className="prop-select" /></Field>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Prazo relativo (dias após a data programada)">
                <input type="number" min="0" value={form.due_offset_days} onChange={(e) => set("due_offset_days", e.target.value)} data-testid="rec-due-offset" className="prop-select" />
              </Field>
              <Field label="Política de sobreposição">
                <select value={form.overlap_policy} onChange={(e) => set("overlap_policy", e.target.value)} data-testid="rec-overlap" className="prop-select">
                  {Object.entries(REC_OVERLAP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Término">
              <div className="space-y-2" data-testid="rec-end">
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="end_type" checked={form.end_type === "never"} onChange={() => set("end_type", "never")} data-testid="rec-end-never" /> Sem data final</label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="end_type" checked={form.end_type === "date"} onChange={() => set("end_type", "date")} data-testid="rec-end-date-radio" /> Até a data
                  <input type="date" disabled={form.end_type !== "date"} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="rec-end-date" className="h-8 rounded-md border border-slate-200 px-2 text-sm disabled:bg-slate-100" />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="end_type" checked={form.end_type === "count"} onChange={() => set("end_type", "count")} data-testid="rec-end-count-radio" /> Após
                  <input type="number" min="1" disabled={form.end_type !== "count"} value={form.end_count} onChange={(e) => set("end_count", e.target.value)} data-testid="rec-end-count" className="h-8 w-20 rounded-md border border-slate-200 px-2 text-sm disabled:bg-slate-100" /> ocorrências
                </label>
              </div>
            </Field>

            {isEdit && (
              <Field label="Próxima data programada (opcional)">
                <input type="date" value={form.next_date ? form.next_date.slice(0, 10) : ""} onChange={(e) => set("next_date", e.target.value)} data-testid="rec-next-date" className="prop-select" />
              </Field>
            )}
          </section>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="rec-submit" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">{isEdit ? "Salvar" : "Criar recorrência"}</button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
    {children}
  </label>
);
